import { useState } from 'react';
import { Check } from 'lucide-react';
import type { StripeCheckoutRequestDto, StripeCheckoutResponseDto } from '@primetime/shared/apiContracts';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '../components/ui/PageHeader';
import { membershipPlans, formatPrimeTimeCredits } from '../features/access/catalogAccess';
import { useAuth } from '../features/auth/AuthContext';
import { api, ApiError } from '../services/apiClient';

export function Pricing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [pendingPlanSlug, setPendingPlanSlug] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function handleCheckout(planSlug: StripeCheckoutRequestDto['planSlug']) {
    if (!user) {
      navigate('/login');
      return;
    }

    setPendingPlanSlug(planSlug);
    setError(null);

    try {
      const response = await api<StripeCheckoutResponseDto>('/api/billing/stripe/checkout', {
        method: 'POST',
        body: JSON.stringify({ planSlug } satisfies StripeCheckoutRequestDto)
      });
      window.location.assign(response.checkoutUrl);
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Unable to start checkout right now.';
      setError(message);
      setPendingPlanSlug(null);
    }
  }

  return (
    <section className="container-shell py-16">
      <PageHeader
        title="Membership"
        subtitle="Member Access for a curated Professional DJ/VJ promotional pool."
      />

      <div className="mx-auto mb-8 max-w-4xl rounded-2xl border border-white/10 bg-white/[0.04] p-6 text-center">
        <p className="text-sm font-bold leading-relaxed text-white/70">
          PrimeTime Credits are included with each monthly membership and are used to unlock curated DJ/VJ set tools,
          edits, transitions, remixes, and video mixes. Launch Pricing is shown below.
        </p>
      </div>

      {error ? (
        <div className="mx-auto mb-8 max-w-4xl rounded-2xl border border-prime-red/40 bg-prime-red/10 p-4 text-sm text-red-100">
          {error}
        </div>
      ) : null}

      <div className="mx-auto grid max-w-6xl gap-6 lg:grid-cols-3">
        {membershipPlans.map((plan) => (
          <article key={plan.name} className={`auth-card relative rounded-3xl p-8 ${plan.badge ? 'glass-red' : 'glass'}`}>
            {plan.badge && (
              <span className="absolute right-6 top-6 rounded-full bg-prime-red px-3 py-1 text-xs font-black uppercase tracking-wider">
                {plan.badge}
              </span>
            )}
            <h2 className="pr-24 text-2xl font-black">{plan.name}</h2>
            <p className="mt-3 min-h-[44px] text-sm text-white/55">{plan.description}</p>

            <div className="mt-6">
              <span className="text-4xl font-black">{plan.price}</span>
              <span className="text-white/50">{plan.cadence}</span>
            </div>

            <ul className="mt-8 space-y-4">
              <li className="flex items-center gap-3 text-white/80">
                <Check className="h-5 w-5 text-prime-red" />
                {formatPrimeTimeCredits(plan.credits)} monthly
              </li>
              <li className="flex items-center gap-3 text-white/80">
                <Check className="h-5 w-5 text-prime-red" />
                Credits expire after {plan.expirationDays} days
              </li>
              <li className="flex items-center gap-3 text-white/80">
                <Check className="h-5 w-5 text-prime-red" />
                Unlock catalog selections
              </li>
            </ul>

            <button
              className="btn-primary mt-8 w-full disabled:opacity-50"
              disabled={pendingPlanSlug !== null}
              onClick={() => void handleCheckout(plan.slug)}
            >
              {pendingPlanSlug === plan.slug ? 'Redirecting...' : user ? 'Start Membership Checkout' : 'Log In to Subscribe'}
            </button>
          </article>
        ))}
      </div>
    </section>
  );
}
